import type {
  ClientPerfHeavyFrame,
  ClientPerfLongStallEvent,
  ClientPerfSnapshot,
} from '@star-snake/shared'

type PerfMetricKey =
  | 'handleBinaryMs'
  | 'renderSnakesMs'
  | 'snakeRendererMs'
  | 'sparkRendererMs'
  | 'portalRendererMs'
  | 'particlesMs'
  | 'deathScatterMs'
  | 'minimapMs'
  | 'hudMs'

interface PerformanceMemoryLike {
  usedJSHeapSize: number
}

type PerfGlobal = typeof globalThis & {
  __STAR_SNAKE_CLIENT_PERF__?: ClientPerfSnapshot
}

const PERF_ALPHA = 0.12
const MEMORY_SAMPLE_INTERVAL_MS = 5000
const PUBLISH_INTERVAL_MS = 1000
const HEAVY_FRAME_RING_SIZE = 32
const MIN_HEAVY_FRAME_DT_MS = 24
const LONG_STALL_RING_SIZE = 12
const LONG_STALL_DT_MS = 500

export class ClientPerfObserver {
  private avgFrameDtMs = 0
  private maxFrameDtMs = 0
  private avgGameLoopMs = 0
  private avgUnattributedFrameMs = 0
  private gcPauseCount = 0
  private heapUsedMb: number | null = null
  private lastMemorySampleMs = 0
  private lastPublishMs = 0
  private currentFrameAttributedMs = 0
  private readonly metrics: Record<PerfMetricKey, number> = {
    handleBinaryMs: 0,
    renderSnakesMs: 0,
    snakeRendererMs: 0,
    sparkRendererMs: 0,
    portalRendererMs: 0,
    particlesMs: 0,
    deathScatterMs: 0,
    minimapMs: 0,
    hudMs: 0,
  }
  private latestSnapshot: ClientPerfSnapshot | null = null
  private counts = {
    sparkStateCount: 0,
    sparkSpriteCount: 0,
    remoteSnakeCount: 0,
    snakeGraphicsCount: 0,
    portalFadeCount: 0,
    particleCount: 0,
    deathScatterCount: 0,
    pendingBinaryCount: 0,
    droppedPendingBinaryCount: 0,
  }
  private readonly heavyFrames = new Array<ClientPerfHeavyFrame | null>(HEAVY_FRAME_RING_SIZE).fill(null)
  private heavyFrameWriteIndex = 0
  private heavyFrameCount = 0
  private readonly longStalls = new Array<ClientPerfLongStallEvent | null>(LONG_STALL_RING_SIZE).fill(null)
  private longStallWriteIndex = 0
  private longStallCount = 0
  private worstLongStallMs = 0

  beginFrame(dtMs: number, nowMs: number): void {
    this.currentFrameAttributedMs = 0
    if (dtMs > 0) {
      if (this.avgFrameDtMs > 0 && dtMs > Math.max(MIN_HEAVY_FRAME_DT_MS, this.avgFrameDtMs * 2)) {
        this.gcPauseCount++
      }
      this.maxFrameDtMs = Math.max(this.maxFrameDtMs, dtMs)
      this.avgFrameDtMs = this.avgFrameDtMs === 0
        ? dtMs
        : this.avgFrameDtMs * (1 - PERF_ALPHA) + dtMs * PERF_ALPHA
    }

    if (nowMs - this.lastMemorySampleMs >= MEMORY_SAMPLE_INTERVAL_MS) {
      this.lastMemorySampleMs = nowMs
      const memory = (performance as Performance & { memory?: PerformanceMemoryLike }).memory
      this.heapUsedMb = memory ? memory.usedJSHeapSize / (1024 * 1024) : null
    }
  }

  record(metric: PerfMetricKey, durationMs: number): void {
    if (!Number.isFinite(durationMs) || durationMs < 0) return
    this.currentFrameAttributedMs += durationMs
    const prev = this.metrics[metric]
    this.metrics[metric] = prev === 0
      ? durationMs
      : prev * (1 - PERF_ALPHA) + durationMs * PERF_ALPHA
  }

  endFrame(gameLoopMs: number): void {
    if (!Number.isFinite(gameLoopMs) || gameLoopMs < 0) return
    const unattributedFrameMs = Math.max(0, gameLoopMs - this.currentFrameAttributedMs)
    this.avgGameLoopMs = this.avgGameLoopMs === 0
      ? gameLoopMs
      : this.avgGameLoopMs * (1 - PERF_ALPHA) + gameLoopMs * PERF_ALPHA
    this.avgUnattributedFrameMs = this.avgUnattributedFrameMs === 0
      ? unattributedFrameMs
      : this.avgUnattributedFrameMs * (1 - PERF_ALPHA) + unattributedFrameMs * PERF_ALPHA
  }

  setCounts(counts: Partial<typeof this.counts>): void {
    this.counts = { ...this.counts, ...counts }
  }

  captureFrame(nowMs: number, frameDtMs: number): void {
    if (!Number.isFinite(frameDtMs) || frameDtMs <= 0) return
    const heavyThreshold = Math.max(MIN_HEAVY_FRAME_DT_MS, this.avgFrameDtMs * 2)
    if (frameDtMs < heavyThreshold) return

    this.heavyFrames[this.heavyFrameWriteIndex] = {
      updatedAtMs: nowMs,
      frameDtMs,
      avgFrameDtMs: this.avgFrameDtMs,
      gameLoopMs: this.avgGameLoopMs,
      unattributedFrameMs: this.avgUnattributedFrameMs,
      handleBinaryMs: this.metrics.handleBinaryMs,
      renderSnakesMs: this.metrics.renderSnakesMs,
      snakeRendererMs: this.metrics.snakeRendererMs,
      sparkRendererMs: this.metrics.sparkRendererMs,
      portalRendererMs: this.metrics.portalRendererMs,
      particlesMs: this.metrics.particlesMs,
      deathScatterMs: this.metrics.deathScatterMs,
      minimapMs: this.metrics.minimapMs,
      hudMs: this.metrics.hudMs,
      sparkStateCount: this.counts.sparkStateCount,
      sparkSpriteCount: this.counts.sparkSpriteCount,
      remoteSnakeCount: this.counts.remoteSnakeCount,
      snakeGraphicsCount: this.counts.snakeGraphicsCount,
      portalFadeCount: this.counts.portalFadeCount,
      particleCount: this.counts.particleCount,
      deathScatterCount: this.counts.deathScatterCount,
      pendingBinaryCount: this.counts.pendingBinaryCount,
      droppedPendingBinaryCount: this.counts.droppedPendingBinaryCount,
    }
    this.heavyFrameWriteIndex = (this.heavyFrameWriteIndex + 1) % HEAVY_FRAME_RING_SIZE
    this.heavyFrameCount = Math.min(HEAVY_FRAME_RING_SIZE, this.heavyFrameCount + 1)
  }

  isLongStall(frameDtMs: number): boolean {
    return Number.isFinite(frameDtMs) && frameDtMs >= LONG_STALL_DT_MS
  }

  recordLongStall(event: ClientPerfLongStallEvent): void {
    this.longStalls[this.longStallWriteIndex] = event
    this.longStallWriteIndex = (this.longStallWriteIndex + 1) % LONG_STALL_RING_SIZE
    this.longStallCount++
    this.worstLongStallMs = Math.max(this.worstLongStallMs, event.stallDtMs)
  }

  publish(
    nowMs: number,
    fps: number,
    snapshotIntervalMs: number,
    network: {
      inputAckMs: number
      rttMs: number
      avgAckOverRttMs: number
      avgInputSendGapMs: number
      maxInputSendGapMs: number
      avgTouchToSendAgeMs: number | null
      avgTouchMoveGapMs: number | null
      pendingInputAckCount: number
      wsBufferedAmount: number
    },
  ): void {
    if (nowMs - this.lastPublishMs < PUBLISH_INTERVAL_MS) return
    this.lastPublishMs = nowMs

    const snapshot: ClientPerfSnapshot = {
      updatedAtMs: nowMs,
      fps,
      avgFrameDtMs: this.avgFrameDtMs,
      maxFrameDtMs: this.maxFrameDtMs,
      avgGameLoopMs: this.avgGameLoopMs,
      avgUnattributedFrameMs: this.avgUnattributedFrameMs,
      avgHandleBinaryMs: this.metrics.handleBinaryMs,
      avgRenderSnakesMs: this.metrics.renderSnakesMs,
      avgSnakeRendererMs: this.metrics.snakeRendererMs,
      avgSparkRendererMs: this.metrics.sparkRendererMs,
      avgPortalRendererMs: this.metrics.portalRendererMs,
      avgParticlesMs: this.metrics.particlesMs,
      avgDeathScatterMs: this.metrics.deathScatterMs,
      avgMinimapMs: this.metrics.minimapMs,
      avgHudMs: this.metrics.hudMs,
      gcPauseCount: this.gcPauseCount,
      heapUsedMb: this.heapUsedMb,
      sparkStateCount: this.counts.sparkStateCount,
      sparkSpriteCount: this.counts.sparkSpriteCount,
      remoteSnakeCount: this.counts.remoteSnakeCount,
      snakeGraphicsCount: this.counts.snakeGraphicsCount,
      portalFadeCount: this.counts.portalFadeCount,
      particleCount: this.counts.particleCount,
      deathScatterCount: this.counts.deathScatterCount,
      pendingBinaryCount: this.counts.pendingBinaryCount,
      droppedPendingBinaryCount: this.counts.droppedPendingBinaryCount,
      snapshotIntervalMs,
      inputAckMs: network.inputAckMs,
      rttMs: network.rttMs,
      avgAckOverRttMs: network.avgAckOverRttMs,
      avgInputSendGapMs: network.avgInputSendGapMs,
      maxInputSendGapMs: network.maxInputSendGapMs,
      avgTouchToSendAgeMs: network.avgTouchToSendAgeMs,
      avgTouchMoveGapMs: network.avgTouchMoveGapMs,
      pendingInputAckCount: network.pendingInputAckCount,
      wsBufferedAmount: network.wsBufferedAmount,
      longStallCount: this.longStallCount,
      worstLongStallMs: this.worstLongStallMs,
      recentLongStalls: this.getLongStalls(),
      heavyFrames: this.getHeavyFrames(),
    }
    this.latestSnapshot = snapshot
    ;(globalThis as PerfGlobal).__STAR_SNAKE_CLIENT_PERF__ = snapshot
  }

  getLatestSnapshot(): ClientPerfSnapshot | null {
    return this.latestSnapshot
  }

  dispose(): void {
    this.latestSnapshot = null
    delete (globalThis as PerfGlobal).__STAR_SNAKE_CLIENT_PERF__
  }

  private getHeavyFrames(): ClientPerfHeavyFrame[] {
    if (this.heavyFrameCount <= 0) return []
    const result: ClientPerfHeavyFrame[] = []
    const start = (this.heavyFrameWriteIndex - this.heavyFrameCount + HEAVY_FRAME_RING_SIZE) % HEAVY_FRAME_RING_SIZE
    for (let i = 0; i < this.heavyFrameCount; i++) {
      const entry = this.heavyFrames[(start + i) % HEAVY_FRAME_RING_SIZE]
      if (entry) result.push(entry)
    }
    return result
  }

  private getLongStalls(): ClientPerfLongStallEvent[] {
    const retained = Math.min(this.longStallCount, LONG_STALL_RING_SIZE)
    if (retained <= 0) return []
    const result: ClientPerfLongStallEvent[] = []
    const start = (this.longStallWriteIndex - retained + LONG_STALL_RING_SIZE) % LONG_STALL_RING_SIZE
    for (let i = 0; i < retained; i++) {
      const entry = this.longStalls[(start + i) % LONG_STALL_RING_SIZE]
      if (entry) result.push(entry)
    }
    return result
  }
}
